import {
  EnvConfig,
  Media,
  MediaType,
  normalizeUrl,
  useAuthentication,
  useGrippContext,
} from '@gripp/shared-logic';
import {
  Colors,
  ArrowTopRightAngleIcon,
  colors,
  SaveIcon,
  tokens
} from '@gripp/shared-ui';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

type FilePreviewHeaderActionsProps = {
  media: Media | null;
};

export const FilePreviewHeaderActions = ({
  media,
}: FilePreviewHeaderActionsProps) => {
  const authentication = useAuthentication();

  if (!media) return null;

  const handleAction = async () => {
    if (media.type === MediaType.Link) {
      if (media.url) {
        const normalizedUrl = normalizeUrl(media.url);
        window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    // Download document or audio
    if (media.type === MediaType.Document) {
      const documentUrl = `${EnvConfig.apiUrl}/document/${media.id}`;
      try {
        const headers: HeadersInit = {};
        if (authentication?.accessToken) {
          headers['Authorization'] = `Bearer ${authentication.accessToken}`;
        }
        const response = await fetch(documentUrl, {
          method: 'GET',
          headers: { ...headers, Accept: 'application/json' },
          credentials: 'omit',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.downloadUrl) {
            const fileResponse = await fetch(data.downloadUrl);
            if (fileResponse.ok) {
              const fileBlob = await fileResponse.blob();
              const downloadLink = document.createElement('a');
              downloadLink.href = URL.createObjectURL(fileBlob);
              downloadLink.download = media.name || 'document';
              downloadLink.style.display = 'none';
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              URL.revokeObjectURL(downloadLink.href);
              return;
            }
            window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
            return;
          }
        }
      } catch {
        // fallback to opening the URL directly
      }
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    } else {
      const fileUrl = `${EnvConfig.mediaUrl}/media/${media.id}`;
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const isLink = media.type === MediaType.Link;

  return (
    <View style={styles.headerActions}>
      <Pressable
        onPress={handleAction}
        style={styles.actionButton}
        accessibilityRole="button"
        accessibilityLabel={isLink ? 'Open link in new tab' : 'Download file'}
      >
        {isLink ? (
          <ArrowTopRightAngleIcon size={24} color={Colors.role.textPrimary} />
        ) : (
          <SaveIcon size={24} color={Colors.role.textPrimary} />
        )}
      </Pressable>
    </View>
  );
};

type FilePreviewPanelProps = {
  media: Media | null;
};

export const FilePreviewPanel = ({ media }: FilePreviewPanelProps) => {
  const authentication = useAuthentication();
  const { logger } = useGrippContext();
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [isLoadingPdfPreview, setIsLoadingPdfPreview] = useState(false);

  const isPdfDocument =
    media?.type === MediaType.Document &&
    media.extension?.toLowerCase() === 'pdf';

  useEffect(() => {
    let previewObjectUrl: string | null = null;
    let isCancelled = false;

    const loadPdfPreview = async () => {
      if (!isPdfDocument || !media?.id) {
        setPdfPreviewUrl(null);
        setIsLoadingPdfPreview(false);
        return;
      }

      if (authentication?.isLoading) {
        setIsLoadingPdfPreview(true);
        return;
      }

      setIsLoadingPdfPreview(true);
      try {
        const headers: HeadersInit = {};
        if (authentication?.accessToken) {
          headers['Authorization'] = `Bearer ${authentication.accessToken}`;
        }

        const response = await fetch(`${EnvConfig.apiUrl}/document/${media.id}`, {
          method: 'GET',
          headers: { ...headers, Accept: 'application/json' },
          credentials: 'omit',
        });

        if (!response.ok) {
          throw new Error('Failed to resolve PDF preview URL');
        }

        const data = (await response.json()) as { downloadUrl?: string };
        if (!data.downloadUrl) {
          throw new Error('Download URL not found');
        }

        const fileResponse = await fetch(data.downloadUrl);
        if (!fileResponse.ok) {
          throw new Error('Failed to fetch PDF file');
        }

        const fileBlob = await fileResponse.blob();
        if (typeof URL?.createObjectURL === 'function') {
          if (isCancelled) {
            return;
          }
          const objectUrl = URL.createObjectURL(fileBlob);
          if (isCancelled && typeof URL?.revokeObjectURL === 'function') {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          previewObjectUrl = objectUrl;
        }

        if (!isCancelled) {
          setPdfPreviewUrl(previewObjectUrl);
        }
      } catch (error) {
        if (!isCancelled) {
          setPdfPreviewUrl(null);
        }
        logger?.error(error, `Failed to generate PDF preview for ${media?.id}`);
      } finally {
        if (!isCancelled) {
          setIsLoadingPdfPreview(false);
        }
      }
    };

    void loadPdfPreview();

    return () => {
      isCancelled = true;
      if (previewObjectUrl && typeof URL?.revokeObjectURL === 'function') {
        URL.revokeObjectURL(previewObjectUrl);
      }
    };
  }, [authentication?.accessToken, authentication?.isLoading, isPdfDocument, logger, media?.id]);

  if (!media) return null;

  return (
    <View style={styles.container}>
      {isPdfDocument && pdfPreviewUrl ? (
        <View style={styles.pdfPreviewContainer} testID="pdf-preview-frame">
          <iframe
            src={pdfPreviewUrl}
            title={media.name ?? 'PDF preview'}
            style={styles.pdfFrame}
          />
        </View>
      ) : (
        <View style={styles.content}>
          <Text style={[tokens.typography.title.small, styles.fileName]}>
            {media.name}
          </Text>
          {isPdfDocument && isLoadingPdfPreview ? (
            <ActivityIndicator color={Colors.role.textSoft} />
          ) : (
            <Text style={[tokens.typography.body.medium, styles.unableText]}>
              {t('asset.files.unableToPreview')}
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.role.canvas,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  fileName: {
    textAlign: 'center',
    color: Colors.role.textStrong,
  },
  unableText: {
    color: Colors.role.textSoft,
    textAlign: 'center',
  },
  pdfPreviewContainer: {
    width: '100%',
    height: '100%',
  },
  pdfFrame: {
    width: '100%',
    height: '100%',
    borderWidth: 0,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 8,
  },
  actionButton: {
    padding: 8,
    margin: 0,
    borderRadius: 8,
    height: 24,
    width: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
